import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sc_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

/* -----------------------------------------------------------------------------
 * Business-loan pricing model
 * Indicative APR-based amortised monthly repayment. Final rate is bespoke.
 * Default indicative APR: 8.9% — a mid-market rate for unsecured business loans.
 * -------------------------------------------------------------------------- */
export const DEFAULT_INDICATIVE_APR = 0.089;

export function businessLoanCost(amount, months, apr = DEFAULT_INDICATIVE_APR) {
  if (!amount || !months || months <= 0) return { monthly: 0, total: 0, interest: 0, apr };
  const r = apr / 12;
  const factor = Math.pow(1 + r, months);
  const monthly = (amount * r * factor) / (factor - 1);
  const total = monthly * months;
  const interest = total - amount;
  return { monthly, interest, total, apr };
}

/* Kept as an alias so any residual imports don't break. */
export const paydayCost = businessLoanCost;

/* -----------------------------------------------------------------------------
 * Log-scale slider helpers — maps a linear slider position (0–1000)
 * to a currency amount between £5,000 and £500,000,000.
 * -------------------------------------------------------------------------- */
export const AMOUNT_MIN = 5000;
export const AMOUNT_MAX = 500_000_000;
export const SLIDER_STEPS = 1000;

const LN_MIN = Math.log(AMOUNT_MIN);
const LN_MAX = Math.log(AMOUNT_MAX);

function snapAmount(raw) {
  if (raw < 10_000) return Math.round(raw / 1000) * 1000;
  if (raw < 100_000) return Math.round(raw / 5000) * 5000;
  if (raw < 1_000_000) return Math.round(raw / 25_000) * 25_000;
  if (raw < 10_000_000) return Math.round(raw / 250_000) * 250_000;
  if (raw < 100_000_000) return Math.round(raw / 1_000_000) * 1_000_000;
  return Math.round(raw / 10_000_000) * 10_000_000;
}

export function posToAmount(pos) {
  const p = Math.max(0, Math.min(SLIDER_STEPS, pos));
  const raw = Math.exp(LN_MIN + (LN_MAX - LN_MIN) * (p / SLIDER_STEPS));
  return snapAmount(raw);
}

export function amountToPos(amount) {
  const a = Math.max(AMOUNT_MIN, Math.min(AMOUNT_MAX, amount));
  return Math.round(SLIDER_STEPS * (Math.log(a) - LN_MIN) / (LN_MAX - LN_MIN));
}

/* -----------------------------------------------------------------------------
 * Currency formatting
 * -------------------------------------------------------------------------- */
export function formatGBP(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(Math.round(n || 0));
}
export function formatGBP2(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 2 }).format(n || 0);
}

/**
 * Compact form for large business-loan amounts.
 * £5,000 → £5,000 · £250,000 → £250k · £1,200,000 → £1.2M · £500,000,000 → £500M
 */
export function formatGBPCompact(n) {
  const v = Math.round(Number(n) || 0);
  if (v >= 1_000_000_000) {
    const b = v / 1_000_000_000;
    return `£${b % 1 === 0 ? b.toFixed(0) : b.toFixed(1)}B`;
  }
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    return `£${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (v >= 10_000) return `£${Math.round(v / 1000)}k`;
  return formatGBP(v);
}

export function estimateMonthly(amount, termMonths) {
  return businessLoanCost(amount, termMonths).monthly;
}
